// The only pages allowed to open this app and receive its results (the resident portal).
// useParentMessaging accepts INIT only from these origins and only ever posts back to them.
export const PARENT_ORIGINS = ['https://app.tenantport.com', 'https://app.qa.tenantport.com'];

/**
 * Pure: may `origin` act as our parent? Non-prod builds also accept http://localhost on any port,
 * for running the resident portal locally.
 */
export const isAllowedParentOrigin = (origin: string | null | undefined, appEnv: string | undefined = process.env.REACT_APP_APP_ENV): boolean => {
  if (!origin) return false;
  if (PARENT_ORIGINS.includes(origin)) return true;
  if (appEnv === 'prod') return false;
  try {
    const url = new URL(origin);
    // url.origin === origin rejects anything carrying a path, credentials or other extras.
    return url.protocol === 'http:' && url.hostname === 'localhost' && url.origin === origin;
  } catch {
    return false;
  }
};

/** The parts of a MessageEvent the INIT decision looks at (a MessageEvent satisfies it). */
export interface IncomingMessage {
  origin: string;
  source: unknown;
  data: unknown;
}

/**
 * Pure: is this the parent's INIT? Returns the origin to reply to, or null to ignore the message.
 * Accepts only a message from the window that opened us, on an allowlisted origin, whose
 * self-declared parentOrigin (if any) matches. The reply origin is always event.origin — which
 * the browser vouches for — never data.parentOrigin, which the sender chose.
 */
export const acceptInit = (
  { origin, source, data }: IncomingMessage,
  opener: unknown,
  appEnv: string | undefined = process.env.REACT_APP_APP_ENV
): string | null => {
  if (!opener || source !== opener) return null;
  if (!isAllowedParentOrigin(origin, appEnv)) return null;
  if (!data || typeof data !== 'object' || (data as { type?: unknown }).type !== 'INIT') return null;
  const claimed = (data as { parentOrigin?: unknown }).parentOrigin;
  if (claimed != null && claimed !== origin) return null;
  return origin;
};
